import { escapeHtml } from '../format.js';
import { groupBySection, packColumns, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import { featuredItem } from './cafe-panels.js';
import {
  bilingual,
  description,
  fitScale,
  image,
  priceBlock,
  type MenuTemplate,
  type RenderContext,
} from './common.js';
import {
  BURGER_ICON,
  calloutIds,
  heroPhotoId,
  spBrand,
  spCss,
  spFamily,
  spPanelCss,
  spSlogan,
  spStack,
  spStackColumn,
  SP_THEME,
  SP_VARIANTS,
} from './stack-panels-five.js';

/**
 * Stack Panels, three panels: the featured item stands in the middle panel on black, photo
 * large and its price in a big block of the hot colour, with the sections in a hot-coloured
 * panel on its left and a cream one on its right.
 */

const ID = 'stack-panels-trio';
const S = `.mb-t-${ID}`;
const P = 'sp3';

function hero(ctx: RenderContext): string {
  const item = featuredItem(ctx.board);
  const brand = spBrand(ctx, P);
  if (!item) {
    const photo = image(ctx, heroPhotoId(ctx.board), `${P}-hero-img`);
    return `${brand}${photo ? `<div class="${P}-hero-ph">${photo}</div>` : `<div class="${P}-hero-gap">${BURGER_ICON}</div>`}<div class="${P}-hero-text">${spSlogan(ctx, P, 22)}</div>`;
  }
  const photo = image(ctx, item.imageMediaId, `${P}-hero-img`);
  const label = (ctx.showEn ? item.badge || item.badgeAr : item.badgeAr || item.badge)?.trim();
  const scale = Math.min(fitScale(item.title, 13, 0.5), fitScale(item.titleAr, 16, 0.5));
  return [
    brand,
    photo ? `<div class="${P}-hero-ph">${photo}</div>` : `<div class="${P}-hero-gap">${BURGER_ICON}</div>`,
    `<div class="${P}-hero-text">`,
    label ? `<span class="${P}-hero-tag" dir="auto">${escapeHtml(label)}</span>` : '',
    `<div style="font-size:${scale.toFixed(3)}em">${bilingual(ctx, item.title, item.titleAr, `${P}-hero-name`)}</div>`,
    description(ctx, item, `${P}-hero-desc`),
    `<div class="${P}-hero-price">${priceBlock(ctx, item, `${P}-hp`)}</div>`,
    '</div>',
  ].join('');
}

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const callouts = calloutIds(board, featuredItem(board)?.id);
  const column = spStackColumn(board, callouts, {
    capacity: portrait ? 60 : 59,
    photo: portrait ? 9 : 11,
    head: 5.2,
    nameChars: 24,
    item: { nameEm: 1.45, descEm: 0.85, perLine: 50, gap: 1.15 },
  });
  return [column, column];
}

export const stackPanelsTrio: MenuTemplate = {
  id: ID,
  name: 'Stack panels: three',
  description:
    'Your featured item large in a black middle panel with a big price block, between an orange panel and a cream one listing your sections.',
  defaultTheme: SP_THEME,
  variants: SP_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: spFamily('Three panels'),
  uses: { featured: true, sectionImages: true, brand: true, decorations: ['Photo in the middle panel when there is no featured item'] },

  paginate(board, orientation) {
    const featured = featuredItem(board);
    return packColumns(groupBySection(board, featured?.id), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const callouts = calloutIds(ctx.board, featuredItem(ctx.board)?.id);
    const [left = [], right = []] = ctx.page.columns;
    const panel = (blocks: typeof left, kind: string, side: string) =>
      `<div class="${P}-panel ${P}-k-${kind} ${P}-${side}">${
        blocks.length ? blocks.map((b) => spStack(ctx, b, callouts, P, 14)).join('') : `<div class="${P}-filler">${spSlogan(ctx, P, 18)}</div>`
      }</div>`;
    return [
      `<div class="${P}-wrap">`,
      panel(left, 'hot', 'left'),
      `<div class="${P}-hero ${P}-k-black">${hero(ctx)}</div>`,
      panel(right, 'cream', 'right'),
      '</div>',
    ].join('');
  },

  css: `
${spCss(S, P)}
${spPanelCss(S, P)}
${S} .${P}-wrap{flex:1;min-height:0;display:grid;grid-template-columns:31fr 38fr 31fr}
${S}.mb-portrait .${P}-wrap{grid-template-columns:1fr 1fr;grid-template-rows:40fr 60fr}
${S}.mb-portrait .${P}-hero{grid-column:1 / -1;grid-row:1}
${S}.mb-portrait .${P}-left{grid-row:2}
${S} .${P}-panel{--${P}-px:2.6em}
${S} .${P}-filler{flex:1;display:flex;align-items:flex-end}
${S} .${P}-filler .${P}-slogan{color:var(--${P}-head)}
${S} .${P}-hero{position:relative;min-width:0;min-height:0;overflow:hidden;display:flex;flex-direction:column;padding:3em 3em 3em;gap:.8em}
${S} .${P}-hero-ph{position:relative;flex:1;min-height:0;margin:0 -3em}
${S} .${P}-hero-gap{flex:1;min-height:0;display:flex;align-items:center;justify-content:center;color:var(--mb-primary);opacity:.22}
${S} .${P}-hero-gap svg{width:55%;max-height:80%}
${S} .${P}-hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  -webkit-mask-image:linear-gradient(transparent,#000 16%,#000 78%,transparent);mask-image:linear-gradient(transparent,#000 16%,#000 78%,transparent)}
${S} .${P}-hero-text{position:relative;display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:-3em}
${S} .${P}-hero-tag{background:var(--mb-primary);color:var(--${P}-on-hot);font-family:var(--mb-font-heading);font-size:1.1em;
  letter-spacing:.14em;text-transform:uppercase;padding:.15em .8em;margin-bottom:.6em}
${S} .${P}-hero-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:4.6em;line-height:1;
  text-transform:uppercase;color:var(--mb-primary)}
${S} .${P}-hero-name.mb-2nd{font-size:2.6em;color:var(--mb-text);margin-top:.15em}
${S} .${P}-hero-desc{font-size:1.1em;opacity:.8;margin-top:.6em;max-width:28em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .${P}-hero-price{margin-top:1em;display:flex;justify-content:center}
${S} .${P}-hp-price{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:3.6em;line-height:1.1;
  background:var(--mb-primary);color:var(--${P}-on-hot);padding:.05em .45em;white-space:nowrap}
${S} .${P}-hp-pricebox{display:flex;align-items:center;gap:.8em}
${S} .${P}-hp-old{font-family:var(--mb-font-heading);font-size:2em;opacity:.6;white-space:nowrap}
${S} .${P}-hp-sizes{display:flex;gap:.6em;flex-wrap:wrap;justify-content:center}
${S} .${P}-hp-size{display:flex;flex-direction:column;align-items:center}
${S} .${P}-hp-size small{font-size:1em;font-weight:800;letter-spacing:.14em;text-transform:uppercase;margin-bottom:.2em}
${S} .${P}-hp-size .${P}-hp-price{font-size:2.6em}
${S}.mb-portrait .${P}-hero{display:grid;grid-template-columns:1fr 1fr;grid-template-rows:auto 1fr;padding:3em;gap:1em 2em}
${S}.mb-portrait .${P}-brandblock{grid-column:1;grid-row:1;align-items:flex-start;text-align:start}
${S}.mb-portrait .${P}-lockup{justify-content:flex-start}
${S}.mb-portrait .${P}-hero-text{grid-column:1;grid-row:2;align-self:center;align-items:flex-start;text-align:start;margin:0}
${S}.mb-portrait .${P}-hero-price{justify-content:flex-start}
${S}.mb-portrait .${P}-hero-ph,${S}.mb-portrait .${P}-hero-gap{grid-column:2;grid-row:1 / span 2;margin:-3em -3em -3em 0}
${S}.mb-portrait .${P}-hero-img{-webkit-mask-image:linear-gradient(to right,transparent,#000 30%);mask-image:linear-gradient(to right,transparent,#000 30%)}
${S}.mb-portrait[dir=rtl] .${P}-hero-img{-webkit-mask-image:linear-gradient(to left,transparent,#000 30%);mask-image:linear-gradient(to left,transparent,#000 30%)}
${S}.mb-portrait[dir=rtl] .${P}-hero-ph{margin:-3em 0 -3em -3em}
`,
};
